/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GenAILiveClient } from "../lib/genai-live-client";
import { LiveClientOptions } from "../types";
import { AudioStreamer } from "../lib/audio-streamer";
import { audioContext } from "../lib/utils";
import VolMeterWorket from "../lib/worklets/vol-meter";
import { LiveConnectConfig, Modality } from "@google/genai";
import { fetchGeminiToken, getBackendUrl } from "../lib/token-service";

export type UseLiveAPIResults = {
  client: GenAILiveClient;
  setConfig: (config: LiveConnectConfig) => void;
  config: LiveConnectConfig;
  model: string;
  setModel: (model: string) => void;
  connected: boolean;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  stopAudio: () => void;
  volume: number;
  audioStreamer: AudioStreamer | null;
};

export function useLiveAPI(options: LiveClientOptions): UseLiveAPIResults {
  // Use a ref to hold the actual client instance
  const clientRef = useRef<GenAILiveClient | null>(null);
  
  // CRITICAL FIX: Use state variable to force re-renders when client changes
  // React refs don't trigger re-renders, so we need this to update the useMemo
  const [clientVersion, setClientVersion] = useState(0);
  
  // Get the current client or create a placeholder for return type
  // The actual client will be created when connect() is called with the ephemeral token
  const client = useMemo(() => {
    console.log(`🔄 [CLIENT] useMemo triggered - clientVersion: ${clientVersion}`);
    if (clientRef.current) {
      console.log(`✅ [CLIENT] Returning real client (version ${clientVersion}), clientId: ${clientRef.current.clientId}`);
      return clientRef.current;
    }
    // Create placeholder with token to avoid "legacy mode" message
    // This placeholder will be replaced by the real client on first connect()
    console.log(`⚠️ [CLIENT] Creating placeholder client (no real client yet)`);
    const placeholder = new GenAILiveClient({ token: "placeholder-will-be-replaced-on-connect" });
    console.log(`⚠️ [CLIENT] Placeholder clientId: ${placeholder.clientId}`);
    return placeholder;
  }, [clientVersion]); // Now depends on clientVersion which DOES trigger re-renders
  
  const audioStreamerRef = useRef<AudioStreamer | null>(null);

  const [model, setModel] = useState<string>("models/gemini-2.5-flash-native-audio-preview-09-2025");
  const [config, setConfig] = useState<LiveConnectConfig>({});
  const [connected, setConnected] = useState(false);
  const [volume, setVolume] = useState(0);

  // register audio for streaming server -> speakers
  useEffect(() => {
    if (!audioStreamerRef.current) {
      audioContext({ id: "audio-out" }).then((audioCtx: AudioContext) => {
        audioStreamerRef.current = new AudioStreamer(audioCtx);
        audioStreamerRef.current
          .addWorklet<any>("vumeter-out", VolMeterWorket, (ev: any) => {
            setVolume(ev.data.volume);
          })
          .then(() => {
            // Successfully added worklet
          });
      });
    }
  }, [audioStreamerRef]);

  const connect = useCallback(async () => {
    if (!config) {
      throw new Error("config has not been set");
    }
    
    try {
      console.log("🔌 [GEMINI] Attempting to connect to Gemini Live API via use-live-api hook");
      
      // Fetch ephemeral token from backend
      console.log("🔑 [TOKEN] Fetching ephemeral token from backend...");
      const backendUrl = getBackendUrl();
      const tokenData = await fetchGeminiToken(backendUrl, "property_evaluation");
      
      console.log("✅ [TOKEN] Token fetched successfully, creating new client with token");
      
      // Disconnect old client if exists
      if (clientRef.current) {
        clientRef.current.disconnect();
      }
      
      // Create new client with token
      const newClientOptions: LiveClientOptions = {
        token: tokenData.token
      };
      const newClient = new GenAILiveClient(newClientOptions);
      
      // Setup event listeners for the new client
      const onOpen = () => {
        console.log("🔓 [GEMINI] Connection opened - setting connected state to true");
        setConnected(true);
      };

      const onClose = () => {
        console.log("🔒 [GEMINI] Connection closed - setting connected state to false");
        setConnected(false);
      };

      const onError = (error: ErrorEvent) => {
        console.error("❌ [GEMINI] Connection error:", error);
      };

      const stopAudioStreamer = () => {
        console.log("🔇 [GEMINI] Audio streaming interrupted");
        audioStreamerRef.current?.stop();
      };

      const onAudio = (data: ArrayBuffer) => {
        // console.log(`🔊 [GEMINI] Received audio data from Gemini: ${data.byteLength} bytes`);
        audioStreamerRef.current?.addPCM16(new Uint8Array(data));
      };

      newClient
        .on("error", onError)
        .on("open", onOpen)
        .on("close", onClose)
        .on("interrupted", stopAudioStreamer)
        .on("audio", onAudio);
      
      // Update the client ref
      clientRef.current = newClient;
      
      // CRITICAL FIX: Increment clientVersion to trigger useMemo re-computation
      // This ensures components get the new client reference
      console.log(`🔄 [CLIENT] Updating clientVersion to trigger useMemo re-computation`);
      setClientVersion(prev => {
        const newVersion = prev + 1;
        console.log(`✅ [CLIENT] clientVersion updated: ${prev} -> ${newVersion}, new clientId: ${newClient.clientId}`);
        return newVersion;
      });
      
      // Use the model from backend response
      const modelToUse = tokenData.model;
      console.log("📋 [GEMINI] Model from token:", modelToUse);
      console.log("⚙️ [GEMINI] Using minimal config - system_instruction, tools, speech_config locked in ephemeral token");
      console.log("🔒 [GEMINI] Token contains: system_instruction, response_modalities, speech_config, tools");
      
      // Connect using the new token client with minimal config
      // The responseModalities must match what's locked in the backend token
      // Backend locked: responseModalities: ['AUDIO']
      await newClient.connect(modelToUse, {
        responseModalities: [Modality.AUDIO]
      });
      console.log("✅ [GEMINI] Connection established via use-live-api hook with ephemeral token");
      
      // Update the model state
      setModel(modelToUse);
    } catch (error) {
      console.error("❌ [GEMINI] Failed to connect:", error);
      throw error;
    }
  }, [config]);

  const disconnect = useCallback(async () => {
    console.log("🔌 [GEMINI] Disconnecting from Gemini Live API via use-live-api hook");
    // Stop audio streaming immediately
    audioStreamerRef.current?.stop();
    // Disconnect the client if it exists
    if (clientRef.current) {
      clientRef.current.disconnect();
    }
    setConnected(false);
    console.log("✅ [GEMINI] Disconnected from Gemini Live API via use-live-api hook");
  }, [setConnected]);

  const stopAudio = useCallback(() => {
    console.log("🔇 [GEMINI] Stopping audio streaming");
    audioStreamerRef.current?.stop();
  }, []);

  return {
    client,
    config,
    setConfig,
    model,
    setModel,
    connected,
    connect,
    disconnect,
    stopAudio,
    volume,
    audioStreamer: audioStreamerRef.current,
  };
}
